import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex, MultiSelect, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getAvailableDealsForFund,
	linkFundToDeal,
} from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { UserRowWithActions } from '@dealstack/frontend/shared/components/UserRowWithActions/UserRowWithActions.tsx';
import {
	GENERIC_ELEMENT_ID,
	makeComplexDataTestId,
	TEST_ELEMENT_IDS,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	fundId: string;
	fundName: string;
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const LinkFundDrawer = ({ fundId, fundName, opened, onClose, onSuccess }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const [selectedDealIds, setSelectedDealIds] = useState<string[]>([]);
	const [lastAddedDealName, setLastAddedDealName] = useState<string | null>(null);

	const linkMutation = useMutation({
		mutationFn: (variables: Parameters<typeof linkFundToDeal>[0]) => linkFundToDeal(variables),
	});

	const { data, isFetching } = useQuery({
		queryKey: ['availableDealsForFund', { fundId }],
		queryFn: ({ signal }) => getAvailableDealsForFund({ fundId }, signal),
		enabled: opened,
	});

	const availableDeals = useMemo(() => data ?? [], [data]);

	const dealOptions = useMemo(
		() =>
			availableDeals.map((deal) => ({
				label: deal.name,
				value: deal.id,
			})),
		[availableDeals]
	);

	const hasNoAvailableDeals = !dealOptions.length && !isFetching;

	useEffect(() => {
		if (!opened) {
			setSelectedDealIds([]);
			setLastAddedDealName(null);
		}
	}, [opened]);

	const handleDealSelectionChange = (value: string[]) => {
		const lastAddedId = value[value.length - 1];
		const name = dealOptions.find(({ value }) => value === lastAddedId)?.label ?? null;
		setLastAddedDealName(name);
		setTimeout(() => setLastAddedDealName(null), 3000);
		setSelectedDealIds(value);
	};

	const removeDealFromSelection = (dealId: string) => {
		setSelectedDealIds((prev) => prev.filter((id) => id !== dealId));
	};

	const selectedDeals = availableDeals.filter((deal) => selectedDealIds.includes(deal.id));

	const onSubmit = async () => {
		if (!selectedDealIds.length) {
			return;
		}

		try {
			await Promise.all(
				selectedDealIds.map((dealId) => linkMutation.mutateAsync({ fundId, dealId }))
			);

			showToastV2({
				title: tAdminDashboard('fund_link_drawer.toast.success.title'),
				message: tAdminDashboard('fund_link_drawer.toast.success.message'),
			});
			onClose();
			onSuccess?.();
		} catch {
			showToastV2({
				title: tAdminDashboard('fund_link_drawer.toast.error.title'),
				message: tAdminDashboard('fund_link_drawer.toast.error.message'),
				state: 'error',
			});
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tAdminDashboard('fund_link_drawer.title')}
		>
			<Flex
				direction="column"
				justify="space-between"
				h="100%"
				data-testid={TEST_ELEMENT_IDS.LINK_FUND_DRAWER}
			>
				<Flex direction="column" gap="1rem">
					<MultiSelect
						data-testid={TEST_ELEMENT_IDS.LINK_FUND_DRAWER_DEALS_SELECT}
						data={dealOptions}
						renderOption={({ option }) => (
							<div
								data-testid={makeComplexDataTestId(
									GENERIC_ELEMENT_ID.SELECT_OPTION,
									option.value
								)}
							>
								{option.label}
							</div>
						)}
						value={selectedDealIds}
						onChange={handleDealSelectionChange}
						label={
							<Flex align="center" justify="space-between" gap="0.25rem">
								{tAdminDashboard('fund_link_drawer.form.deal_label')}
								{lastAddedDealName && (
									<Flex align="center" gap="0.25rem" maw="60%">
										<Icon variant="holdings-checkmark" size="md" />
										<Flex align="center" gap="0.25rem">
											<Text fz="sm" fw={400} truncate lineClamp={1}>
												{lastAddedDealName}
											</Text>
											<Text
												fz="sm"
												fw={400}
												c="dark.0"
												style={{ whiteSpace: 'nowrap' }}
											>
												{tAdminDashboard(
													'fund_link_drawer.form.added_to_list'
												)}
											</Text>
										</Flex>
									</Flex>
								)}
							</Flex>
						}
						labelProps={{
							style: { width: '100%' },
						}}
						placeholder={tAdminDashboard('fund_link_drawer.form.deal_placeholder')}
						disabled={hasNoAvailableDeals}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						size="md"
						clearable
						multiple
						searchable
					/>

					{hasNoAvailableDeals && (
						<InfoBox
							iconVariant="information"
							alignContent="center"
							description={tAdminDashboard('fund_link_drawer.no_deals_warning')}
						/>
					)}

					{!!selectedDeals.length && (
						<Flex direction="column">
							<Text fw={600}>
								{tAdminDashboard('fund_link_drawer.selected_title')}
							</Text>
							<Text c="gray.9" mt="0.25rem" mb="0.5rem">
								{tAdminDashboard('fund_link_drawer.selected_description', {
									fundName,
								})}
							</Text>
							{selectedDeals.map((deal) => (
								<UserRowWithActions
									key={deal.id}
									displayName={deal.name}
									onUserDeleteClick={() => removeDealFromSelection(deal.id)}
									hasEditPermission={true}
									showInitialsAvatar={false}
								/>
							))}
						</Flex>
					)}
				</Flex>

				<Button
					data-testid={TEST_ELEMENT_IDS.LINK_FUND_DRAWER_SUBMIT}
					onClick={onSubmit}
					disabled={linkMutation.isPending || !selectedDealIds.length}
				>
					{tAdminDashboard('fund_link_drawer.link_button')}
				</Button>
			</Flex>
		</Drawer>
	);
};
